import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import VectorSource from 'ol/source/Vector';
import { TileWMS, WMTS } from 'ol/source';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { TileLayer, VectorLayer } from 'os-elements';
import { mapMenuTabKeys, mapModes } from 'client/utils/constants/appStates';
import { ASSET_TYPES } from 'client/utils/constants/assetTypes';
import { hierarchy } from 'd3-hierarchy';
import { apiBaseUrl } from 'client/utils/constants/frontEndConfig';
import { withAuth } from 'client/pages/MapPage/layers/sources/BaseMap';
import useDpi from '../../hooks/useDpi';
import { INCHES_PER_METER } from '../../hooks/useMapScale';
import { selectSectorSpecificStyles } from 'client/store/layersSlice/layersSlice';

const { DEFAULT, LOADING } = mapModes;
const tileSourceCache = new Map();

// This will allow raster layers from external data sources to be added in the future
const applyApiUrl = url => {
    let apiUrl = url;
    let serverType = '';
    let crossOrigin = '';
    if (!url.includes('https://')) {
        apiUrl = apiBaseUrl + url; // assume that API Manager is used if full URL not provided
        serverType = 'geoserver'; // NUAR asset raster data is provided by GeoServer
        crossOrigin = 'use-credentials'; // disables CORB warnings on localhost
    }
    return { apiUrl, serverType, crossOrigin };
};

// Return the appropriate layer components based on the supported sourceTypes (WMTS, WMS, and Vector)
const WMTSAssetLayer = ({ node, visible, aoiBoundingBox, mapMode, dpi, className }) => {
    const { assetType, id, zIndex, sourceProperties, minScale, maxScale } = node;
    const minRes = minScale / (INCHES_PER_METER * dpi);
    const maxRes = maxScale / (INCHES_PER_METER * dpi);
    const format = sourceProperties?.format;
    const layer = sourceProperties?.layer;
    const matrixSet = sourceProperties?.matrixSet;
    const projection = sourceProperties?.projection;
    const tileGrid = sourceProperties?.tileGrid;
    const url = sourceProperties?.url;

    const sourceKey = useMemo(() => {
        if (!format || !layer || !matrixSet || !projection || !tileGrid || !url) {
            return null;
        }

        return JSON.stringify({
            format,
            layer,
            matrixSet,
            projection,
            tileGrid,
            url
        });
    }, [format, layer, matrixSet, projection, tileGrid, url]);

    const source = useMemo(() => {
        if (!aoiBoundingBox || mapMode.includes(LOADING) || !sourceKey) {
            return null;
        }

        if (tileSourceCache.has(sourceKey)) {
            return tileSourceCache.get(sourceKey);
        }

        const { apiUrl } = applyApiUrl(url);
        const nextSource = new WMTS({
            url: apiUrl,
            projection,
            layer,
            matrixSet,
            format,
            tileGrid: new WMTSTileGrid(JSON.parse(tileGrid)),
            tileLoadFunction: withAuth
        });

        tileSourceCache.set(sourceKey, nextSource);
        return nextSource;
    }, [aoiBoundingBox, mapMode, projection, layer, matrixSet, format, sourceKey, tileGrid, url]);

    if (!source || !aoiBoundingBox || mapMode.includes(LOADING)) {
        return null;
    }

    return <TileLayer
        key={id}
        id={assetType}
        visible={visible}
        extent={aoiBoundingBox}
        source={source}
        className={assetType.includes('raster') ? className : null}
        zIndex={zIndex || 0}
        minResolution={minRes}
        maxResolution={maxRes}
    />;
};

const WMSAssetLayer = ({ node, visible, aoiBoundingBox, mapMode, dpi }) => {
    const { assetType, id, zIndex, sourceProperties, minScale, maxScale } = node;
    const minRes = minScale / (INCHES_PER_METER * dpi);
    const maxRes = maxScale / (INCHES_PER_METER * dpi);
    const layer = sourceProperties?.layer;
    const projection = sourceProperties?.projection;
    const url = sourceProperties?.url;

    const sourceKey = useMemo(() => {
        if (!layer || !projection || !url) {
            return null;
        }

        return JSON.stringify({
            layer,
            projection,
            url
        });
    }, [layer, projection, url]);

    const source = useMemo(() => {
        if (!aoiBoundingBox || mapMode.includes(LOADING) || !sourceKey) {
            return null;
        }

        if (tileSourceCache.has(sourceKey)) {
            return tileSourceCache.get(sourceKey);
        }

        const { apiUrl, serverType, crossOrigin } = applyApiUrl(url);
        const nextSource = new TileWMS({
            url: apiUrl,
            serverType,
            crossOrigin,
            projection,
            params: {
                LAYERS: layer
            },
            tileLoadFunction: withAuth
        });

        tileSourceCache.set(sourceKey, nextSource);
        return nextSource;
    }, [aoiBoundingBox, layer, mapMode, projection, sourceKey, url]);

    if (!source || !aoiBoundingBox || mapMode.includes(LOADING)) {
        return null;
    }

    return <TileLayer
        key={id}
        id={assetType}
        visible={visible}
        extent={aoiBoundingBox}
        source={source}
        zIndex={zIndex || 0}
        minResolution={minRes}
        maxResolution={maxRes}
    />;
};

const VectorAssetLayer = ({ node, features, visible, dpi, sectorSpecificStyles, className }) => {
    const { assetType, id, zIndex, style, minScale, maxScale } = node;
    const minRes = minScale / (INCHES_PER_METER * dpi);
    const maxRes = maxScale / (INCHES_PER_METER * dpi);

    if (!features) {
        return null;
    }

    return <VectorLayer
        key={id}
        id={assetType}
        visible={visible}
        source={new VectorSource({ features })}
        className={assetType === ASSET_TYPES.OBSERVATION ? className : null}
        style={feature => style(feature, sectorSpecificStyles)}
        zIndex={zIndex || 0}
        minResolution={minRes}
        maxResolution={maxRes}
    />;
};

const AssetLayers = ({ hiddenAssets, className }) => {
    const mapMode = useSelector(state => state?.map?.mode);
    const hierarchy = useSelector(state => state?.layers?.result?.hierarchy);
    const assetsByLayer = useSelector(state => state?.assets?.layers || {});
    const filteredAssetsByLayer = useSelector(state => state?.assets?.filteredLayers);
    const selectedOwner = useSelector(state => state?.map?.ownerId);
    const selectedTab = useSelector(state => state?.map?.menuSelectedTab);
    const showFiltered = selectedTab === mapMenuTabKeys.ISOLATED_VIEWS && selectedOwner;
    const filteredAssets = showFiltered ? filteredAssetsByLayer || {} : assetsByLayer;
    const { dpi } = useDpi();
    const aoiSource = useSelector(state => state.aoi?.source);
    const toggledLayers = useSelector(state => state.layers?.toggledLayers);
    const sectorSpecificStyles = useSelector(selectSectorSpecificStyles);
    const aoiDrawn = aoiSource && aoiSource.getFeatures().length > 0;
    const aoiBoundingBox = aoiDrawn ? aoiSource.getExtent() : null;
    const vectorStyleVersion = `${showFiltered}-${Boolean(sectorSpecificStyles?.enabled)}-${sectorSpecificStyles?.sector || 'none'}`;

    useEffect(() => {
        Object.values(filteredAssets || {}).forEach((layerFeatures) => {
            if (Array.isArray(layerFeatures)) {
                layerFeatures.forEach((feature) => {
                    feature?.setStyle?.(undefined);
                    feature?.changed?.();
                });
            }
        });
    }, [vectorStyleVersion, filteredAssets]);

    if (!mapMode.includes(DEFAULT) || !hierarchy?.leaves) {
        return null;
    }

    return hierarchy.leaves().map(node => {
        const { assetType, checked, id, sourceType } = node.data;
        const features = filteredAssets[assetType] || null;
        const visible = checked && !hiddenAssets.includes(assetType) && (showFiltered || toggledLayers.includes(assetType));

        if (sourceType === 'WMTS') {
            return <WMTSAssetLayer key={id} node={node.data} visible={visible} aoiBoundingBox={aoiBoundingBox} mapMode={mapMode} dpi={dpi} className={className} />;
        }

        if (sourceType === 'WMS') {
            return <WMSAssetLayer key={id} node={node.data} visible={visible} aoiBoundingBox={aoiBoundingBox} mapMode={mapMode} dpi={dpi} />;
        }

        if (sourceType === 'Vector') {
            return <VectorAssetLayer key={id} node={node.data} features={features} visible={visible} dpi={dpi} sectorSpecificStyles={sectorSpecificStyles} className={className} />;
        }

        return null;
    });
};

AssetLayers.propTypes = {
    mapMode: PropTypes.array,
    assetsByLayer: PropTypes.object,
    filteredAssetsByLayer: PropTypes.object,
    hierarchy: PropTypes.instanceOf(hierarchy),
    hiddenAssets: PropTypes.arrayOf(PropTypes.string),
    className: PropTypes.string
};

AssetLayers.defaultProps = {
    hiddenAssets: [],
    className: ''
};

export default AssetLayers;
